(() => {
  const SELECTORS = {
    header: 'header-b',
    trigger: '[data-trigger]',
    megamenu: '[data-megamenu]',
    backdrop: '[data-backdrop]',
    close: '[data-close]',
    categoryItem: '[data-category-target]',
    petPanel: '[data-pet-panel]',
    healthPanel: '[data-health-panel]',
    mobileToggle: '[data-mobile-toggle]',
    mobileDrawer: '[data-mobile-drawer]',
    mobileClose: '[data-mobile-close]',
    dropdown: '[data-dropdown]',
    linkItem: '[data-mega-menu], [data-dropdown]',
  };

  const CLASSES = {
    open: 'is-open',
    visible: 'is-visible',
    active: 'is-active',
    noScroll: 'header-b-no-scroll',
  };

  const HOVER_OPEN_DELAY = 80;
  const HOVER_CLOSE_DELAY = 250;


  class MegaMenuController {
    constructor(headerEl) {
      this.header = headerEl;
      this.triggers = headerEl.querySelectorAll(SELECTORS.trigger);
      this.megamenus = headerEl.querySelectorAll(SELECTORS.megamenu);
      this.backdrop = headerEl.querySelector(SELECTORS.backdrop);

      this.activeKey = null;
      this.hoveredKey = null;
      this.openTimer = null;
      this.closeTimer = null;
      this.rafPending = false;
      this.lastX = -1;
      this.lastY = -1;

      this.bind();
    }

    bind() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (e) => this.handleClick(e, trigger));
      });

      document.addEventListener('mousemove', (e) => this.onMouseMove(e), { passive: true });
      document.addEventListener('mouseleave', () => this.onPointerLeftDocument());

      this.header.querySelectorAll(SELECTORS.categoryItem).forEach((btn) => {
        btn.addEventListener('mouseenter', (e) => this.switchCategory(e, btn));
        btn.addEventListener('click', (e) => this.switchCategory(e, btn));
      });

      this.header.querySelectorAll(SELECTORS.close).forEach((btn) => {
        btn.addEventListener('click', () => this.closeAll());
      });

      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => this.closeAll());
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeAll();
      });
    }

    onMouseMove(e) {
      this.lastX = e.clientX;
      this.lastY = e.clientY;
      if (this.rafPending) return;
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        this.evaluateHoverState();
      });
    }

    onPointerLeftDocument() {
      this.hoveredKey = null;
      this.scheduleClose();
    }

    evaluateHoverState() {
      const x = this.lastX;
      const y = this.lastY;
      if (x < 0 || y < 0) return;

      const newKey = this.detectHoveredKey(x, y);

      if (newKey === this.hoveredKey) return; 
      this.hoveredKey = newKey;

      if (newKey) {
        if (this.activeKey !== newKey) {
          this.scheduleOpen(newKey);
        } else {
          this.cancelClose();
        }
      } else {
        this.scheduleClose();
      }
    }

    detectHoveredKey(x, y) {
      for (const trigger of this.triggers) {
        const item = trigger.closest(SELECTORS.linkItem);
        if (!item) continue;
        const key = trigger.dataset.trigger;
        const rect = item.getBoundingClientRect();
        if (
          x >= rect.left - 4 &&
          x <= rect.right + 4 &&
          y >= rect.top &&
          y <= rect.bottom + 20
        ) {
          return key;
        }
      }

      if (this.activeKey) {
        const activeMega = this.header.querySelector(`[data-megamenu="${this.activeKey}"]`);
        if (activeMega && !activeMega.hidden) {
          const rect = activeMega.getBoundingClientRect();
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            return this.activeKey;
          }
        }
        const activeItem = this.header.querySelector(`[data-trigger="${this.activeKey}"]`)?.closest('[data-dropdown]');
        if (activeItem) {
          const dropdown = activeItem.querySelector('.header-b__dropdown');
          if (dropdown) {
            const rect = dropdown.getBoundingClientRect();
            if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
              return this.activeKey;
            }
          }
        }
      }

      return null;
    }

    scheduleOpen(key) {
      this.cancelClose();
      clearTimeout(this.openTimer);
      if (this.activeKey === key) return;
      this.openTimer = setTimeout(() => {
        const trigger = this.header.querySelector(`[data-trigger="${key}"]`);
        if (trigger) this.openMenu(trigger);
      }, HOVER_OPEN_DELAY);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeAll(), HOVER_CLOSE_DELAY);
    }

    cancelClose() {
      clearTimeout(this.closeTimer);
    }

    openMenu(trigger) {
      const key = trigger.dataset.trigger;
      this.activeKey = key;

      // lock background scroll
      document.documentElement.style.overflow = 'hidden';

      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', t === trigger ? 'true' : 'false');
      });

      this.megamenus.forEach((mega) => {
        if (mega.dataset.megamenu === key) {
          mega.hidden = false;
          void mega.offsetHeight;
          mega.classList.add(CLASSES.open);
        } else {
          mega.classList.remove(CLASSES.open);
          mega.hidden = true;
        }
      });

      if (this.backdrop && (key === 'pet' || key === 'health')) {
        this.backdrop.hidden = false;
        void this.backdrop.offsetHeight;
        this.backdrop.classList.add(CLASSES.visible);
      }
    }

    closeAll() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.activeKey = null;

      document.documentElement.style.overflow = '';

      this.triggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });

      this.megamenus.forEach((mega) => {
        mega.classList.remove(CLASSES.open);
        setTimeout(() => {
          if (!mega.classList.contains(CLASSES.open)) mega.hidden = true;
        }, 200);
      });

      if (this.backdrop) {
        this.backdrop.classList.remove(CLASSES.visible);
        setTimeout(() => {
          if (!this.backdrop.classList.contains(CLASSES.visible)) this.backdrop.hidden = true;
        }, 200);
      }
    }

    handleClick(e, trigger) {
      e.preventDefault();
      const key = trigger.dataset.trigger;
      if (this.activeKey === key) {
        this.closeAll();
      } else {
        this.openMenu(trigger);
      }
    }

    switchCategory(e, btn) {
      e.preventDefault();
      const targetId = btn.dataset.categoryTarget;
      if (!targetId) return;

      const tablist = btn.closest('[role="tablist"]');
      const wrapper = btn.closest(SELECTORS.megamenu);
      if (!tablist || !wrapper) return;

      tablist.querySelectorAll('[role="tab"]').forEach((tab) => {
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
      });
      btn.setAttribute('aria-selected', 'true');
      btn.tabIndex = 0;

      const panels = wrapper.querySelectorAll('[data-pet-panel], [data-health-panel]');
      panels.forEach((panel) => {
        const pid = panel.dataset.petPanel || panel.dataset.healthPanel;
        if (pid === targetId) {
          panel.classList.add(CLASSES.active);
          panel.hidden = false;
        } else {
          panel.classList.remove(CLASSES.active);
          panel.hidden = true;
        }
      });
    }
  }

  class MobileDrawerController {
    constructor() {
      this.toggle = document.querySelector(SELECTORS.mobileToggle);
      this.drawer = document.querySelector(SELECTORS.mobileDrawer);
      if (!this.toggle || !this.drawer) return;
      this.closeButtons = this.drawer.querySelectorAll(SELECTORS.mobileClose);
      this.bind();
    }

    bind() {
      this.toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
      this.closeButtons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
    }

    isOpen() {
      return this.drawer.classList.contains(CLASSES.open);
    }

    open() {
      this.drawer.classList.add(CLASSES.open);
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.removeAttribute('inert');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add(CLASSES.noScroll);
      const focusable = this.drawer.querySelector('button, a, [tabindex]:not([tabindex="-1"])');
      if (focusable) focusable.focus();
    }

    close() {
      this.drawer.classList.remove(CLASSES.open);
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove(CLASSES.noScroll);
      this.toggle.focus();
    }
  }

  class HeaderB extends HTMLElement {
    connectedCallback() {
      this.megaMenuController = new MegaMenuController(this);
      this.updateBottomVar();
      window.addEventListener('resize', () => this.updateBottomVar());
      window.addEventListener('scroll', () => this.updateBottomVar(), { passive: true });
    }

    updateBottomVar() {
      const rect = this.getBoundingClientRect();
      this.style.setProperty('--header-b-bottom', `${rect.bottom}px`);
    }
  }

  if (!customElements.get('header-b')) {
    customElements.define('header-b', HeaderB);
  }

  const boot = () => {
    new MobileDrawerController();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
